/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type NextApiRequest } from 'next';
import {
  type ContentStackCategoryPages,
  type FormattedContentStackCategoryPages,
} from '../../interface';
import { getContentByContentType } from '../common/getContentByContentType';
import { type PageContextType } from '@/next-types';
import { type ParsedUrlQuery } from 'querystring';
import { deepFilterAndSegmentContent } from '../../utils/deepFilterAndSegmentContent';
import { type YMMSelectorOptions } from '../../interface/models';

export const getCategoryContent = async (
  req: NextApiRequest | PageContextType['req'],
  query: ParsedUrlQuery,
  userSegments: string[],
  ymmSelectorOptions: YMMSelectorOptions,
): Promise<FormattedContentStackCategoryPages> => {
  const response = await getContentByContentType<ContentStackCategoryPages>({
    contentTypeUid: 'category_pages',
    referenceFieldPath: [
      'categorypagebody.banner_split.banner_split',
      'categoryresultheader.banner_split.banner_split',
      'categoryresultheader.deals_split.deals_split',
      'categorypagebody.banner.banner',
      'categoryresultheader.banner.banner',
      'categoryresultheader.pencil_banner.pencil_banner',
      'categoryresultbottom.pencil_banner.pencil_banner',
      'categorypagebody.grid_banner.grid_banner',
      'categorypagebody.image_content.image_content',
      'categorypagebody.image_content_background.image_content_background',
      'categorypagebody.image_category_grid.image_category_grid',
      'categorypagebody.blogtype_grid.blogtype_grid',
      'categorypagebody.faq.faq',
      'categorypagebody.featured_content.featured_content',
      'categorypagebody.product_grid.product_grid',
      'categorypagebody.product_chart.product_chart',
      'categorypagebody.rich_text_editor.rich_text_editor',
      'categorypagebody.video_content.video_content',
      'categorypagebody.parttype_grid.parttype_grid',
      'categorypagebody.pencil_banner.pencil_banner',
      'prefooter.prefooter',
      'categorypagebody.target_div.target_div',
      'categorypagebody.category_grid.category_grid',
      'categorypagebody.citrus_ad.citrus_ad',
      'categorypagebody.image_video_split.image_video_split',
      'categorypagebody.store_locator.store_locator',
    ],
    req,
    query,
  });

  const taxonomyPath = Array.isArray(query.taxonomyPath)
    ? `/${query.taxonomyPath.join('/')}`
    : query.taxonomyPath;

  const { categorypagebody, categoryresultbottom, categoryresultheader, prefooter } = response;

  let pageBody = categorypagebody;
  let resultBottom = categoryresultbottom;
  let resultHeader = categoryresultheader;

  if (taxonomyPath) {
    pageBody =
      deepFilterAndSegmentContent({
        item: categorypagebody,
        taxonomyPath,
        userSegments,
        ymmSelectorOptions,
        exactMatch: true,
      }) ?? [];
    resultBottom =
      deepFilterAndSegmentContent({
        item: categoryresultbottom,
        taxonomyPath,
        userSegments,
        exactMatch: true,
        ymmSelectorOptions,
      }) ?? [];
    resultHeader =
      deepFilterAndSegmentContent({
        item: categoryresultheader,
        taxonomyPath,
        userSegments,
        ymmSelectorOptions,
        exactMatch: true,
      }) ?? [];

    return {
      categorypagebody: Array.isArray(pageBody) ? pageBody : [pageBody],
      categoryresultbottom: Array.isArray(resultBottom) ? resultBottom : [resultBottom],
      categoryresultheader: Array.isArray(resultHeader) ? resultHeader : [resultHeader],
      prefooter,
    };
  }
  return {
    categorypagebody,
    categoryresultbottom,
    categoryresultheader,
    prefooter,
  };
};
